import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Mail, Send, Play, Settings as SettingsIcon, Activity } from "lucide-react";

const TAB_DEFS = [
  { k: "overview", l: "Overview" },
  { k: "campaigns", l: "Campaigns" },
  { k: "queue", l: "Message Queue" },
  { k: "settings", l: "Settings" },
];

const EMPTY_CAMP = {
  name: "", trigger: "user_registered", subject: "", body_md: "",
  delay_hours: 0, audience_filter: null, is_active: true,
  cta_label: "Open KhojByte", cta_url: "",
};

const PRESET_TEMPLATES = [
  {
    name: "Welcome — researcher onboarding",
    trigger: "user_registered", delay_hours: 0,
    subject: "Welcome to KhojByte, {{first_name}} 👋",
    body_md: "Hi {{first_name}},\n\nThanks for joining KhojByte Research Lab — we help researchers turn ideas into published papers.\n\nA quick orientation:\n\n- **Submit your idea** in the dashboard to start a tracked project.\n- **Request mentor guidance** for 1:1 sessions.\n- Explore our **Research Idea Center** for curated topics.\n\nReply to this email if you'd like to talk to a consultant.",
    cta_label: "Open dashboard", cta_url: "/dashboard",
  },
  {
    name: "Idea submitted — thank you",
    trigger: "idea_submitted", delay_hours: 0,
    subject: "We've received your research idea — what's next",
    body_md: "Hi {{first_name}},\n\nThanks for sharing your idea: **{{idea_title}}**.\n\nOur admin team is now reviewing it. You'll typically get a response within 48 hours. In the meantime you can:\n\n- Browse our [Journal Database](https://www.khojbyte.research/dashboard/journals)\n- Watch a [paper-writing course](https://www.khojbyte.research/dashboard/courses)",
    cta_label: "Track your idea", cta_url: "/dashboard",
  },
  {
    name: "Nudge — 3-day inactive",
    trigger: "no_activity_3d", delay_hours: 72,
    subject: "Need a hand getting started, {{first_name}}?",
    body_md: "Hi {{first_name}},\n\nWe noticed you haven't submitted an idea yet. Most researchers start by either:\n\n- Submitting their thesis topic as an idea, or\n- Requesting a free 15-minute mentor consultation.\n\nReply and tell us your research question — we'll point you to the right starting point.",
    cta_label: "Submit your idea", cta_url: "/dashboard/ideas/new",
  },
  {
    name: "Payment receipt + next steps",
    trigger: "payment_completed", delay_hours: 0,
    subject: "Payment received — NPR {{amount_npr}}",
    body_md: "Hi {{first_name}},\n\nWe've received your payment of **NPR {{amount_npr}}**. A signed invoice will follow shortly.\n\nIf this was for guidance, your assigned mentor will reach out within 24 hours. If it was for a course or event, access is now unlocked in your dashboard.",
    cta_label: "View dashboard", cta_url: "/dashboard",
  },
];

function CampaignEditor({ initial, triggers, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body_md.trim()) {
      toast.error("Name, subject and body are required"); return;
    }
    try {
      if (form.id) await api.put(`/admin/email-campaigns/${form.id}`, form);
      else await api.post("/admin/email-campaigns", form);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="kb-card p-6" data-testid="campaign-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{form.id ? "EDITING" : "NEW CAMPAIGN"}</div>
        <button onClick={onCancel} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className="kb-input text-sm sm:col-span-2" placeholder="Campaign name *"
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name-input" />
        <select className="kb-input text-sm" value={form.trigger}
          onChange={(e) => setForm({ ...form, trigger: e.target.value })} data-testid="campaign-trigger-select">
          {triggers.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="number" min="0" className="kb-input text-sm" placeholder="Delay (hours after trigger)"
          value={form.delay_hours} onChange={(e) => setForm({ ...form, delay_hours: (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 0)) })} />
        <input className="kb-input text-sm sm:col-span-2" placeholder="Subject (supports {{first_name}} etc.) *"
          value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="campaign-subject-input" />
        <textarea rows={10} className="kb-input text-sm sm:col-span-2 resize-y font-mono" placeholder="Body in Markdown *"
          value={form.body_md} onChange={(e) => setForm({ ...form, body_md: e.target.value })} data-testid="campaign-body-textarea" />
        <input className="kb-input text-sm" placeholder="CTA label (e.g. Open dashboard)"
          value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} />
        <input className="kb-input text-sm" placeholder="CTA URL (relative or full)"
          value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} />
        <label className="flex items-center gap-2 text-sm text-kb-ink-muted">
          <input type="checkbox" checked={!!form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          Active (triggers will enqueue this campaign)
        </label>
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted sm:col-span-2">
          Template variables: <code className="text-kb-primary">{"{{first_name}}, {{name}}, {{email}}, {{idea_title}}, {{amount_npr}}, {{course_id}}, {{event_id}}, {{plan_code}}"}</code>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={save} className="kb-btn-primary text-sm" data-testid="campaign-save-btn">Save campaign</button>
        <button onClick={onCancel} className="kb-btn-outline text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function AdminEmailEnginePage() {
  const [tab, setTab] = useState("overview");
  const [campaigns, setCampaigns] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [oneOff, setOneOff] = useState({ to_email: "", to_name: "", subject: "", body_md: "" });

  const loadAll = async () => {
    try {
      const [c, t, m, s, a] = await Promise.all([
        api.get("/admin/email-campaigns"),
        api.get("/admin/email-triggers"),
        api.get("/admin/email-messages", { params: { limit: 200 } }),
        api.get("/admin/email-settings"),
        api.get("/admin/analytics/overview", { params: { days: 30 } }),
      ]);
      setCampaigns(c.data || []); setTriggers(t.data || []); setMessages(m.data || []);
      setSettings(s.data || {}); setAnalytics(a.data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { loadAll(); }, []);

  const removeCamp = async (id) => {
    if (!window.confirm("Delete this campaign? Queued messages already in the pipeline will still send.")) return;
    await api.delete(`/admin/email-campaigns/${id}`); toast.success("Deleted"); loadAll();
  };
  const seedTemplate = (t) => setEditing({ ...EMPTY_CAMP, ...t });

  const saveSettings = async () => {
    try {
      await api.put("/admin/email-settings", settings);
      toast.success("Saved"); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendOneOff = async () => {
    if (!oneOff.to_email || !oneOff.subject || !oneOff.body_md) { toast.error("All fields required"); return; }
    try {
      await api.post("/admin/email-messages", oneOff);
      toast.success("Email queued + flushed");
      setOneOff({ to_email: "", to_name: "", subject: "", body_md: "" });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resend = async (id) => {
    await api.post(`/admin/email-messages/${id}/send`); toast.success("Queued for re-send"); loadAll();
  };

  return (
    <div data-testid="admin-email-engine-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMAIL ENGINE</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Analytics & <em className="italic text-kb-primary">automated emails</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-3xl">
            Track visitor activity and autonomously follow up with users based on their state — registration, idea submission, guidance request, payment, or inactivity. Personalised via Markdown templates with merge variables.
          </p>
        </div>
      </div>

      <div className="mt-6 border-b border-slate-200 flex gap-1">
        {TAB_DEFS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === t.k ? "border-kb-primary text-kb-primary font-medium" : "border-transparent text-kb-ink-muted hover:text-kb-primary"}`}
            data-testid={`email-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && analytics && (
        <div className="mt-6 space-y-4">
          <div className="grid sm:grid-cols-4 gap-4">
            <Stat label="Total events (30d)" value={analytics.total_events} />
            <Stat label="Unique sessions" value={analytics.unique_sessions} />
            <Stat label="Identified users" value={analytics.unique_users} />
            <Stat label="Campaigns active" value={campaigns.filter((c) => c.is_active).length} />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="kb-card p-5">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CONVERSION FUNNEL (30d)</div>
              <div className="mt-3 space-y-2">
                {(analytics.funnel || []).map((f) => {
                  const max = Math.max(...analytics.funnel.map((x) => x.sessions), 1);
                  const pct = Math.round((f.sessions / max) * 100);
                  return (
                    <div key={f.step}>
                      <div className="flex justify-between text-xs">
                        <span className="text-kb-ink">{f.step}</span>
                        <span className="kb-mono text-[10px] text-kb-ink-muted">{f.sessions}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-sm overflow-hidden mt-0.5">
                        <div className="h-full bg-kb-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="kb-card p-5">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">TOP PAGES (30d)</div>
              <div className="mt-3 space-y-1.5">
                {(analytics.top_pages || []).map((p) => (
                  <div key={p.page} className="flex justify-between text-xs border-b border-slate-100 pb-1">
                    <span className="text-kb-ink truncate flex-1 mr-2">{p.page || "(root)"}</span>
                    <span className="kb-mono text-[10px] text-kb-ink-muted">{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "campaigns" && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setEditing(EMPTY_CAMP)} className="kb-btn-primary text-sm" data-testid="campaign-new-btn">
              <Plus className="w-3.5 h-3.5" /> New campaign
            </button>
            <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">QUICK-START PRESETS:</span>
            {PRESET_TEMPLATES.map((t) => (
              <button key={t.name} onClick={() => seedTemplate(t)} className="kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-200 text-kb-ink-muted hover:text-kb-primary hover:border-kb-primary/40">
                {t.name}
              </button>
            ))}
          </div>

          {editing && (
            <CampaignEditor
              initial={editing} triggers={triggers}
              onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); loadAll(); }}
            />
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {campaigns.map((c) => (
              <div key={c.id} className="kb-card p-5" data-testid={`campaign-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <Mail className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                    <h3 className="font-serif text-lg text-kb-ink mt-2">{c.name}</h3>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary mt-1">{c.trigger}</div>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-2">
                      {c.is_active ? "ACTIVE" : "PAUSED"} · DELAY {c.delay_hours}h
                    </div>
                    <div className="text-sm text-kb-ink mt-3 line-clamp-2">{c.subject}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setEditing(c)} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid={`campaign-${c.id}-edit`}><Edit3 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => removeCamp(c.id)} className="p-1.5 text-rose-600 hover:text-rose-700" data-testid={`campaign-${c.id}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "queue" && (
        <div className="mt-6 space-y-4">
          <div className="kb-card p-5">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-3">SEND A ONE-OFF / BROADCAST</div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input className="kb-input text-sm" placeholder="To email *" value={oneOff.to_email}
                onChange={(e) => setOneOff({ ...oneOff, to_email: e.target.value })} data-testid="oneoff-to-email" />
              <input className="kb-input text-sm" placeholder="To name" value={oneOff.to_name}
                onChange={(e) => setOneOff({ ...oneOff, to_name: e.target.value })} />
              <input className="kb-input text-sm sm:col-span-2" placeholder="Subject *" value={oneOff.subject}
                onChange={(e) => setOneOff({ ...oneOff, subject: e.target.value })} data-testid="oneoff-subject" />
              <textarea rows={4} className="kb-input text-sm sm:col-span-2 resize-none" placeholder="Body (Markdown) *"
                value={oneOff.body_md} onChange={(e) => setOneOff({ ...oneOff, body_md: e.target.value })} data-testid="oneoff-body" />
            </div>
            <button onClick={sendOneOff} className="kb-btn-primary text-sm mt-3" data-testid="oneoff-send"><Send className="w-3.5 h-3.5" /> Queue & send</button>
          </div>

          <div className="kb-card overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RECENT MESSAGES (LAST 200)</div>
            <div className="divide-y divide-slate-100">
              {messages.map((m) => (
                <div key={m.id} className="px-5 py-3 grid grid-cols-12 gap-2 text-xs" data-testid={`msg-${m.id}`}>
                  <div className="col-span-3 text-kb-ink truncate">{m.to_email}</div>
                  <div className="col-span-5 text-kb-ink-muted truncate">{m.subject}</div>
                  <div className="col-span-2 kb-mono text-[10px] uppercase tracking-wider">
                    <span className={
                      m.status === "sent" ? "text-emerald-700"
                      : m.status === "failed" ? "text-rose-600"
                      : "text-kb-ink-muted"
                    }>{m.status}{m.provider ? ` · ${m.provider}` : ""}</span>
                  </div>
                  <div className="col-span-1 kb-mono text-[10px] text-kb-ink-light">{m.trigger}</div>
                  <div className="col-span-1 flex justify-end">
                    {(m.status === "queued" || m.status === "failed") && (
                      <button onClick={() => resend(m.id)} className="text-kb-primary inline-flex items-center gap-1" title="Send now" data-testid={`msg-${m.id}-resend`}>
                        <Play className="w-3 h-3" /> send
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {messages.length === 0 && (
                <div className="px-5 py-10 text-center kb-mono text-xs text-kb-ink-muted">No messages yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="mt-6 kb-card p-6 max-w-2xl">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-3 inline-flex items-center gap-2"><SettingsIcon className="w-3 h-3" /> EMAIL PROVIDER</div>
          <p className="text-xs text-kb-ink-muted mb-4">If a Resend API key is configured, emails will be sent for real. Otherwise they remain logged as <code>provider=mock</code>.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="kb-input text-sm" placeholder="From email" value={settings.from_email || ""}
              onChange={(e) => setSettings({ ...settings, from_email: e.target.value })} data-testid="settings-from-email" />
            <input className="kb-input text-sm" placeholder="From name" value={settings.from_name || ""}
              onChange={(e) => setSettings({ ...settings, from_name: e.target.value })} />
            <input className="kb-input text-sm sm:col-span-2" placeholder="Reply-to email" value={settings.reply_to || ""}
              onChange={(e) => setSettings({ ...settings, reply_to: e.target.value })} />
            <input className="kb-input text-sm sm:col-span-2" placeholder="Resend API key (paste fresh to update)"
              value={settings.resend_api_key || ""}
              onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })} data-testid="settings-resend-key" />
          </div>
          <button onClick={saveSettings} className="kb-btn-primary text-sm mt-4" data-testid="settings-save-btn">Save settings</button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="kb-card p-4">
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{label}</div>
      <div className="font-serif text-3xl text-kb-ink mt-2">{value ?? "—"}</div>
    </div>
  );
}
